/**
 * IHS-12 Popular odds — match-detail swipe carousel of the most-traded
 * markets for the fixture.
 *
 * The carousel is a thin wrapper around `<OddsCarousel>` from the
 * particles layer. Engine ships an array of `DisplayMarket` payloads
 * shaped like the rest of the odds particles, plus optional team names
 * we surface in a header strip above the carousel. When the markets
 * array is empty (finished match, suspended book) the widget self-hides
 * via `OddsCarousel`'s built-in null guard so a finished fixture's page
 * doesn't show an empty card.
 *
 * The standalone `<PopularOddsWidget>` mounted under article headlines
 * lives in P7. This widget covers only the IHS-bundle slot variant.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import { useTenantConfig } from '../../../tenant/useTenantConfig.js';
import OddsCarousel from '../../particles/OddsCarousel.js';
import type { IhsPopularOddsData } from '../../../types/ihs.js';
import { IhsCard } from './IhsCard.js';
import { TeamIdentity } from '../../primitives/TeamIdentity.js';
import styles from './IhsPopularOdds.module.css';

export function IhsPopularOdds({
  data,
}: {
  data: IhsPopularOddsData;
}): ReactElement | null {
  const { t } = useTranslation();
  const tenant = useTenantConfig();
  const decimalSeparator =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market.decimalSeparator
      : '.';

  if (!data.markets || data.markets.length === 0) return null;

  const title =
    data.header?.title ??
    t('ihs.popularOdds.title', { defaultValue: 'Popular markets' });

  const showTeamRow =
    typeof data.homeTeamName === 'string' &&
    typeof data.awayTeamName === 'string';

  return (
    <IhsCard title={title} bodyVariant="tight">
      {showTeamRow && (
        <div className={`${styles.teamRow} stage-row`}>
          <TeamIdentity name={data.homeTeamName ?? ''} size="sm" />
          <span className={styles.vs}>
            {t('common.vs', { defaultValue: 'VS' })}
          </span>
          <TeamIdentity name={data.awayTeamName ?? ''} size="sm" />
        </div>
      )}
      <div className="stage-row">
        <OddsCarousel
          markets={data.markets}
          decimalSeparator={decimalSeparator}
        />
      </div>
    </IhsCard>
  );
}
